import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import classNames from 'classnames';
import { AskRequest } from '@hone-automation/common';
import { prefixClass } from 'lib/utils';
import { usePatchQueueRequestMutation } from 'hooks/queue';
import { IconValidateNew, IconCancel } from 'components/Icons';
import './MobileEditableTitle.scss';

interface MobileEditableTitleProps {
  /** The request whose title to edit */
  requestId: string;
  /** Display title text */
  title: string;
  /** Additional class name for the root element */
  className?: string;
  /** Callback after successful save, receives updated request */
  onSaved?: (data: AskRequest) => void;
}

export const MobileEditableTitle: React.FC<MobileEditableTitleProps> = ({ requestId, title, className, onSaved }) => {
  const prefix = prefixClass('mobile-editable-title');
  const { locationId } = useParams<{ locationId: string }>();
  const inputRef = useRef<HTMLInputElement>(null);

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [tempTitle, setTempTitle] = useState('');

  const patchMutation = usePatchQueueRequestMutation(data => {
    if (data && onSaved) {
      onSaved(data as AskRequest);
    }
  });

  // Reset editing state when request changes
  useEffect(() => {
    setIsEditing(false);
    setTempTitle('');
  }, [requestId]);

  // Auto-focus and select when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleEditStart = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setTempTitle(title);
      setIsEditing(true);
    },
    [title]
  );

  const handleSave = useCallback(async () => {
    if (tempTitle.trim() === '') return;
    setIsSaving(true);
    try {
      await patchMutation.mutateAsync({
        id: requestId,
        title: tempTitle.trim(),
        locationId,
      });
      setIsEditing(false);
    } catch {
      // Stay in editing mode on error
    } finally {
      setIsSaving(false);
    }
  }, [tempTitle, patchMutation, requestId, locationId]);

  const handleCancel = useCallback(() => {
    setTempTitle('');
    setIsEditing(false);
  }, []);

  if (isEditing) {
    return (
      <div className={classNames(prefix(), prefix('--editing'), className)} onClick={e => e.stopPropagation()}>
        <input
          ref={inputRef}
          type="text"
          className={prefix('__input')}
          value={tempTitle}
          onChange={e => setTempTitle(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') handleSave();
            if (e.key === 'Escape') handleCancel();
          }}
          disabled={isSaving}
          placeholder="Enter title..."
        />
        <div className={prefix('__actions')}>
          <button
            type="button"
            className={classNames(prefix('__btn'), prefix('__btn--save'))}
            onClick={handleSave}
            disabled={isSaving}
          >
            <IconValidateNew />
          </button>
          <button
            type="button"
            className={classNames(prefix('__btn'), prefix('__btn--cancel'))}
            onClick={handleCancel}
            disabled={isSaving}
          >
            <IconCancel />
          </button>
        </div>
      </div>
    );
  }

  return (
    <button type="button" className={classNames(prefix(), prefix('__display'), className)} onClick={handleEditStart}>
      {title}
    </button>
  );
};
